import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Loader2, Phone, CheckCircle2, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Rough EUR → local currency rates (admin-tunable later). Used only to display
// and submit the local amount; the canonical price stays EUR server-side.
const EUR_RATES = { XAF: 656, XOF: 656, CDF: 2800, KES: 150, UGX: 4100, TZS: 2900, ZMW: 28, RWF: 1450, GHS: 16, NGN: 1700, SLL: 23000, SLE: 23 };

const FLAG = { CM: '🇨🇲', CI: '🇨🇮', SN: '🇸🇳', CD: '🇨🇩', BJ: '🇧🇯', CG: '🇨🇬', GA: '🇬🇦', KE: '🇰🇪', RW: '🇷🇼', SL: '🇸🇱', UG: '🇺🇬', ZM: '🇿🇲', TZ: '🇹🇿', NG: '🇳🇬', GH: '🇬🇭' };

/**
 * Reusable white-label pawaPay Mobile Money widget. The payer confirms a PIN on
 * their handset — no redirect, never leaves Nucleus Pay.
 *
 * Props:
 *  - authHeader: () => ({ Authorization }) | object
 *  - amountEur: number (canonical price)
 *  - kind: pawaPay deposit kind (school_upgrade_yearly | school_upgrade_monthly | vip_audit | application_fee | tuition)
 *  - entityId: optional (application_id, etc.)
 *  - statementDescription: short label shown on the SMS
 *  - onSuccess: callback when deposit reaches SUCCESS
 */
export default function NucleusPayMobileMoney({
  authHeader, amountEur, amountLocalXaf, kind, entityId, statementDescription = 'UBO Nucleus Pay', onSuccess,
}) {
  const hdr = () => (typeof authHeader === 'function' ? authHeader() : (authHeader || {}));
  const [countryOpts, setCountryOpts] = useState([]);
  const [country, setCountry] = useState('CM');
  const [operators, setOperators] = useState([]);
  const [correspondent, setCorrespondent] = useState('');
  const [phone, setPhone] = useState('');
  const [busy, setBusy] = useState(false);
  const [deposit, setDeposit] = useState(null);
  const [pinInfo, setPinInfo] = useState(null);
  const [showRevive, setShowRevive] = useState(false);
  const pollRef = useRef();
  const reviveRef = useRef();

  // Load live, merchant-enabled countries from pawaPay config
  useEffect(() => {
    axios.get(`${API}/nucleus-pay/pawapay/correspondents`)
      .then((r) => {
        const cs = (r.data.countries || []).map((c) => [c.code, `${FLAG[c.code] || ''} ${c.name}`.trim()]);
        if (cs.length) {
          setCountryOpts(cs);
          if (!cs.find(([c]) => c === country)) setCountry(cs[0][0]);
        }
      })
      .catch(() => setCountryOpts([['CM', '🇨🇲 Cameroun'], ['CI', "🇨🇮 Côte d'Ivoire"], ['SN', '🇸🇳 Sénégal'], ['CD', '🇨🇩 RD Congo']]));
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!country) return;
    axios.get(`${API}/nucleus-pay/pawapay/correspondents`, { params: { country } })
      .then((r) => {
        const ops = r.data.operators || [];
        setOperators(ops);
        setCorrespondent(ops[0]?.code || '');
      })
      .catch(() => setOperators([]));
  }, [country]);

  useEffect(() => {
    if (!deposit?.depositId) return;
    if (['SUCCESS', 'FAILED', 'CANCELLED', 'REVERSED'].includes(deposit.status)) {
      if (deposit.status === 'SUCCESS') { toast.success('Paiement Mobile Money confirmé ✓'); onSuccess?.(deposit); }
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/nucleus-pay/pawapay/deposit/${deposit.depositId}`, { headers: hdr() });
        setDeposit((d) => ({ ...d, ...r.data }));
      } catch (e) { /* keep polling */ }
    }, 4000);
    return () => clearInterval(pollRef.current);
  }, [deposit?.depositId, deposit?.status]); // eslint-disable-line

  // When a deposit becomes pending, fetch operator-specific PIN-prompt
  // instructions (AUTOMATIC vs MANUAL + USSD revive steps) — dynamic per
  // operator/country, sourced live from pawaPay.
  useEffect(() => {
    if (!deposit?.depositId || !correspondent) return;
    if (!['PENDING', 'CREATED'].includes(deposit.status)) return;
    let active = true;
    axios.get(`${API}/nucleus-pay/pawapay/operator-instructions`, { params: { provider: correspondent } })
      .then((r) => { if (active) setPinInfo(r.data); })
      .catch(() => {});
    // After ~12s, reveal the "revive PIN" steps for AUTOMATIC operators
    reviveRef.current = setTimeout(() => active && setShowRevive(true), 12000);
    return () => { active = false; clearTimeout(reviveRef.current); };
  }, [deposit?.depositId]); // eslint-disable-line

  const op = operators.find((o) => o.code === correspondent);
  const currency = amountLocalXaf ? (op?.currency || 'XAF') : (op?.currency || 'XAF');
  const rate = EUR_RATES[currency] || 656;
  // For tuition, the base is XAF. Convert to the selected operator's currency
  // via the EUR peg (XAF/XOF stay identical; CDF/KES/etc. are converted).
  const localAmount = amountLocalXaf
    ? (currency === 'XAF' || currency === 'XOF'
        ? Math.max(5, Math.round(amountLocalXaf / 5) * 5)
        : Math.max(5, Math.round(((amountLocalXaf / 655.957) * rate) / 5) * 5))
    : Math.max(5, Math.round((amountEur * rate) / 5) * 5);

  const initiate = async () => {
    if (!phone || phone.replace(/\D/g, '').length < 8) { toast.error('Numéro Mobile Money invalide'); return; }
    if (!correspondent) { toast.error('Sélectionnez votre opérateur'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/nucleus-pay/pawapay/deposit/init`, {
        msisdn: phone.replace(/\D/g, ''),
        amount: localAmount,
        currency,
        country,
        correspondent,
        kind,
        entity_id: entityId,
        statement_description: statementDescription.slice(0, 22),
      }, { headers: hdr() });
      setDeposit(r.data);
      toast.message('Confirmez le paiement sur votre téléphone (PIN Mobile Money)');
    } catch (e) {
      const detail = e.response?.data?.detail || 'Mobile Money indisponible';
      toast.error(typeof detail === 'string' ? detail : 'Erreur paiement');
    }
    setBusy(false);
  };

  if (deposit && ['PENDING', 'CREATED'].includes(deposit.status)) {
    const isManual = pinInfo?.pin_prompt === 'MANUAL';
    const steps = pinInfo?.instructions?.steps || [];
    const quickLink = pinInfo?.instructions?.quick_link;
    const shortCode = pinInfo?.instructions?.short_code;
    const insTitle = pinInfo?.instructions?.title || 'Vous n\u2019avez pas reçu la demande de PIN ?';
    const nameShown = pinInfo?.name_displayed;
    // MANUAL: show steps right away. AUTOMATIC: show after a short delay (revive).
    const showSteps = (isManual || showRevive) && steps.length > 0;
    return (
      <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl p-6 border-2 border-amber-300 text-center" data-testid="np-mm-pending">
        <Loader2 className="w-12 h-12 animate-spin text-amber-700 mx-auto mb-3" />
        <p className="font-display text-lg font-extrabold text-[#0F1B3D]">Confirmez sur votre téléphone</p>
        {isManual ? (
          <p className="text-sm text-slate-700 mt-1">
            Pour payer <strong>{localAmount.toLocaleString('fr-FR')} {currency}</strong> via {op?.operator},
            suivez les étapes ci-dessous sur le <strong>{phone}</strong>.
          </p>
        ) : (
          <p className="text-sm text-slate-700 mt-1">
            Une demande de code PIN a été envoyée au <strong>{phone}</strong>
            {nameShown ? <> — cherchez la notification de <strong>{nameShown}</strong></> : null}.
            Saisissez votre PIN pour valider <strong>{localAmount.toLocaleString('fr-FR')} {currency}</strong>.
          </p>
        )}

        {showSteps && (
          <div className="mt-4 bg-white rounded-xl border border-amber-200 p-4 text-left" data-testid="np-mm-instructions">
            <p className="text-xs font-extrabold text-[#0F1B3D] mb-2">{insTitle}</p>
            <ol className="space-y-1.5">
              {steps.map((s, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-amber-500 text-white text-[11px] font-bold grid place-items-center mt-0.5">{i + 1}</span>
                  <span>{s}</span>
                </li>
              ))}
            </ol>
            {shortCode && (
              <a href={quickLink || `tel:${shortCode.replace(/#/g, '%23')}`} data-testid="np-mm-ussd-btn"
                 className="mt-3 inline-flex items-center justify-center gap-2 w-full bg-[#0F1B3D] text-white font-bold rounded-lg py-2.5 text-sm">
                <Phone className="w-4 h-4" /> Composer {shortCode}
              </a>
            )}
          </div>
        )}

        {!isManual && !showRevive && (
          <p className="text-[11px] text-amber-700 mt-3">⏱ La demande peut prendre quelques secondes à apparaître…</p>
        )}
        <p className="text-[11px] text-amber-700 mt-2">Statut mis à jour automatiquement · ne fermez pas cette page.</p>
      </div>
    );
  }
  if (deposit && deposit.status === 'SUCCESS') {
    return (
      <div className="bg-emerald-50 rounded-xl p-6 border-2 border-emerald-300 text-center" data-testid="np-mm-success">
        <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
        <p className="font-display text-lg font-extrabold text-emerald-900">Paiement confirmé</p>
      </div>
    );
  }
  if (deposit && ['FAILED', 'CANCELLED'].includes(deposit.status)) {
    return (
      <div className="bg-rose-50 rounded-xl p-6 border-2 border-rose-300 text-center space-y-3" data-testid="np-mm-failed">
        <X className="w-12 h-12 text-rose-600 mx-auto" />
        <p className="font-display text-lg font-extrabold text-rose-900">Paiement non abouti</p>
        <p className="text-sm text-rose-800">{deposit.failureReason || 'Le paiement a échoué ou a été annulé.'}</p>
        <Button onClick={() => { setDeposit(null); setPinInfo(null); setShowRevive(false); }} variant="outline" className="border-rose-400 text-rose-700">Réessayer</Button>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="np-mm-form">
      <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
        <p className="text-[11px] uppercase tracking-widest text-amber-700 font-bold mb-1">Mobile Money instantané · sans redirection</p>
        <p className="text-xs text-slate-700">Vous recevrez une demande de PIN directement sur votre téléphone.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        <div>
          <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">Pays</label>
          <select value={country} onChange={(e) => setCountry(e.target.value)} className="w-full px-3 py-2 border rounded-md text-sm" data-testid="np-mm-country">
            {countryOpts.map(([c, l]) => <option key={c} value={c}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">Opérateur</label>
          <select value={correspondent} onChange={(e) => setCorrespondent(e.target.value)} className="w-full px-3 py-2 border rounded-md text-sm" data-testid="np-mm-operator">
            {operators.map((o) => <option key={o.code} value={o.code}>{o.operator} ({o.currency})</option>)}
          </select>
        </div>
        <div className="sm:col-span-2">
          <label className="text-[11px] font-bold text-slate-600 uppercase tracking-wider">Numéro Mobile Money (international, sans +)</label>
          <Input placeholder="237670000001" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="np-mm-msisdn" />
        </div>
      </div>
      <div className="bg-slate-50 rounded-lg p-3 text-xs text-slate-600 flex items-center justify-between">
        <span>Montant à payer :</span>
        <strong className="text-[#0F1B3D] text-sm">{localAmount.toLocaleString('fr-FR')} {currency}</strong>
      </div>
      <Button onClick={initiate} disabled={busy} className="w-full bg-[#E8B547] hover:bg-[#d9a536] text-[#0F1B3D] font-extrabold" data-testid="np-mm-pay">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Phone className="w-4 h-4 mr-1" /> Payer maintenant via {op?.operator || 'Mobile Money'}</>}
      </Button>
    </div>
  );
}
